import {
  Component,
  OnInit,
  ElementRef,
  inject,
  signal,
  DestroyRef,
  computed,
  ViewChild,
  effect,
} from '@angular/core';
import { MultiSelectComponent } from '@progress/kendo-angular-dropdowns';
import { ActivatedRoute } from '@angular/router';
import { environment } from 'src/environments/environment';
import { filter, map } from 'rxjs/operators';
import { ProductService } from './services/product.service';
import { CommonModule } from '@angular/common';
import { GridModule } from '@progress/kendo-angular-grid';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ChartModule } from '@progress/kendo-angular-charts';
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import { DropDownsModule } from '@progress/kendo-angular-dropdowns';
import { MultiSelectModule } from '@progress/kendo-angular-dropdowns';
import { DatePickerModule } from '@progress/kendo-angular-dateinputs';
import { FormsModule } from '@angular/forms';
import 'hammerjs';
import { PopupModule } from '@progress/kendo-angular-popup';
import { CheckBoxModule } from '@progress/kendo-angular-inputs';
import { OutcomeStatusComponent } from './components/outcome-status/outcome-status.component';
import { DistillationFunnelComponent } from './components/distillation-funnel/distillation-funnel.component';
import { CustomersCardComponent } from './components/customers-card/customers-card.component';
import { SlaAdherenceCardComponent } from './components/sla-adherence-card/sla-adherence-card.component';
import { MttdCardComponent } from './components/mttd-card/mttd-card.component';
import { MttrCardComponent } from './components/mttr-card/mttr-card.component';
import { UnknownTicketsChartComponent } from './components/unknown-tickets-chart/unknown-tickets-chart.component';
import { SubscriptionsChartComponent } from './components/subscriptions-chart/subscriptions-chart.component';
import { InsightsComponent } from './components/subscriptions-chart/components/insights/insights.component';
import { GeneralOrganizationService } from 'src/app/features/admin/organizations/components/general/service/general-organization.service';
import { OrganizationDropdownComponent } from 'src/app/core/components/organization-dropdown/organization-dropdown.component';
import { OrganizationService } from 'src/app/features/admin/organizations/services/organization.service';
import { IndustryResult, Organization, Product } from 'src/app/core/models/entities';
import {
  AnalyticsService,
  AlertStatSummaryResponse,
  PartnerMttdResponse,
  AverageMttrResponse,
  PartnerOrgCountResponse,
  SlaAdherenceResponse,
  ticketTrendsResponse,
  QueryType,
} from 'src/app/core/services/analytics.service';
import { infoCircleIcon, moreVerticalIcon, sparklesIcon, filterIcon } from '@progress/kendo-svg-icons';
import { IconsModule, SVGIcon } from '@progress/kendo-angular-icons';
import { TooltipsModule } from '@progress/kendo-angular-tooltip';

// NEW: Import the reusable analytics filter component and its interfaces
import {
  AnalyticsFilterComponent,
  FilterConfig,
  FilterState,
  TimeRange,
} from 'src/app/core/components/analytics-filter';

@Component({
  selector: 'app-analytics',
  standalone: true,
  imports: [
    CommonModule,
    GridModule,
    ChartModule,
    ButtonsModule,
    DropDownsModule,
    MultiSelectModule,
    DatePickerModule,
    FormsModule,
    PopupModule,
    CheckBoxModule,
    OutcomeStatusComponent,
    DistillationFunnelComponent,
    CustomersCardComponent,
    SlaAdherenceCardComponent,
    MttdCardComponent,
    MttrCardComponent,
    UnknownTicketsChartComponent,
    IconsModule,
    TooltipsModule,
    // NEW: Add the reusable analytics filter component
    AnalyticsFilterComponent,
  ],
  templateUrl: './partner-analytics.page.html',
  styleUrl: './partner-analytics.page.scss',
  providers: [ProductService, AnalyticsService],
})
export class PartnerAnalyticsPage implements OnInit {
  // NEW: This component now uses the reusable AnalyticsFilterComponent for all filtering functionality
  // The old filter controls have been replaced with a single, configurable filter component
  // that provides consistent filtering across all analytics pages.

  @ViewChild('toolsMultiselect') toolsMultiselect!: MultiSelectComponent;
  @ViewChild(OutcomeStatusComponent) outcomeStatusComponent!: OutcomeStatusComponent;

  public infoCircleIcon: SVGIcon = infoCircleIcon;
  public moreVerticalIcon: SVGIcon = moreVerticalIcon;

  // NEW: Filter configuration for the reusable analytics filter component
  public filterConfig: FilterConfig = {
    showTimeRange: true,
    showCustomerFilters: true,
    showServiceFilters: true,
    showToolFilters: true,
    showCustomFilters: false,
    showApplyButton: true,
    showResetButton: true,
  };

  // NEW: Convert existing filter properties to signals for the reusable component
  public selectedTimeRange = signal<TimeRange>({ text: 'Last 30 days', value: '30d' });
  public startDate = signal<Date | null>(new Date(new Date().setDate(new Date().getDate() - 365)));
  public endDate = signal<Date | null>(new Date());

  // Data for SLA Adherence and Outcome Status components
  public organizationId = signal<number>(0); // Will be populated from query params

  public generalOrgService = inject(GeneralOrganizationService);
  public productService = inject(ProductService);
  private destroyRef = inject(DestroyRef);
  private route = inject(ActivatedRoute);
  industries = signal<IndustryResult[]>([]);
  public outcomeStatusData = signal<any>(null);
  public unknownRateData = signal<any>(null);
  public topTicketInfo = signal<any>(null);
  public selectedIndustries = signal<IndustryResult[]>([]);

  public alertStatSummary = signal(new AlertStatSummaryResponse());

  // Dates - UPDATED: startDate and endDate are now signals defined above
  public startDateString: string = '';
  public endDateString: string = '';

  public selectedTools = signal<any[]>([]);

  // Modern Angular 17/18 reactive filtering with signals
  private toolEnabledOrgs = signal<number[]>([]);

  // Computed property for filtered org IDs - automatically updates when dependencies change
  public filteredOrgIds = computed(() => {
    const selectedOrgIds = this.getSelectedOrgIds();
    const selectedTools = this.selectedTools();
    const toolOrgs = this.toolEnabledOrgs();
    const currentOrgId = this.organizationId(); // Capture signal value

    // No tools selected - use normal customer filtering
    if (selectedTools.length === 0) {
      return this.getFallbackOrgIds(selectedOrgIds);
    }

    // Tools selected - find intersection
    if (selectedOrgIds.length > 0) {
      const intersection = selectedOrgIds.filter((id) => toolOrgs.includes(id));

      // Fallback if no intersection
      if (intersection.length === 0) {
        return currentOrgId ? [currentOrgId] : [];
      }

      return intersection;
    }

    // No customers selected, use all tool-enabled orgs
    return toolOrgs.length > 0 ? toolOrgs : currentOrgId ? [currentOrgId] : [];
  });

  public orgService = inject(OrganizationService);
  public secondaryOrgService = new OrganizationService();
  products = signal<Product[]>([]);
  selectedServiceGroups = signal<string[]>([]);

  groupedServices = computed(() => {
    const uniqueGroups = new Set<string>();
    return this.products().filter((product) => {
      if (!uniqueGroups.has(product.group)) {
        uniqueGroups.add(product.group);
        return true;
      }
      return false;
    });
  });

  filteredProductsByService = computed(() => {
    const selectedGroups = this.selectedServiceGroups();

    if (selectedGroups.length === 0) {
      return this.products();
    }

    return this.products().filter((product) => selectedGroups.includes(product.group));
  });

  public onServiceChange(selectedGroups: string[]): void {
    this.selectedTools.set([]);

    setTimeout(() => {
      if (this.toolsMultiselect) {
        this.toolsMultiselect.filterChange.emit('');
        this.toolsMultiselect.data = this.filteredProductsByService();
      }
    });
  }

  public analytics = inject(AnalyticsService);

  public ticketTrends = signal(new ticketTrendsResponse());
  public partnerMttd = signal(new PartnerMttdResponse());
  public averageMttr = signal(new AverageMttrResponse());
  public slaAdherence = signal(new SlaAdherenceResponse());
  public partnerOrgCount = signal(new PartnerOrgCountResponse());
  public selectedOrganizations = signal<Organization[]>([]);
  public organizations = signal<Organization[]>([]);

  // Loading state for distillation funnel
  public isFunnelDataLoading = signal<boolean>(true);

  // Track which data sources have loaded
  private ticketTrendsLoaded = signal<boolean>(false);
  private alertStatSummaryLoaded = signal<boolean>(false);

  hasExplicitSelection = computed(() => {
    const orgsSelected = this.selectedOrganizations().length > 0;
    const toolsSelected = this.selectedTools().length > 0;
    return orgsSelected || toolsSelected;
  });

  queryType = computed(() => {
    return this.hasExplicitSelection() ? QueryType.Filtered : QueryType.All;
  });

  constructor() {
    // Initialize the main orgService state to prevent undefined errors
    this.orgService.state = {
      skip: 0,
      take: 200,
      sort: [{ field: 'name', dir: 'asc' }],
      filter: {
        logic: 'and',
        filters: [],
      },
    };

    this.secondaryOrgService.state = {
      skip: 0,
      take: 200,
      sort: [{ field: 'name', dir: 'asc' }],
      filter: {
        logic: 'and',
        filters: [],
      },
    };

    // Effect 1: Service group changes
    effect(
      () => {
        const selectedServiceGroups = this.selectedServiceGroups();
        this.onServiceChange(selectedServiceGroups);
      },
      { allowSignalWrites: true }
    );

    // Effect 2: Industry changes - affects organization filtering
    effect(
      () => {
        const selectedIndustries = this.selectedIndustries();

        if (!this.orgService.state.filter) {
          this.orgService.state.filter = { logic: 'and', filters: [] };
        }

        // Drop any previous industry filter (flat eq or the multi-industry OR group)
        this.orgService.state.filter.filters = this.orgService.state.filter.filters.filter((f: any) => {
          if (f.field === 'industry') return false;
          if (Array.isArray(f.filters)) return !f.filters.some((sub: any) => sub.field === 'industry');
          return true;
        });

        if (selectedIndustries.length === 0) {
          this.selectedOrganizations.set([]);
          this.orgService.read();
          return;
        }

        if (selectedIndustries.length === 1) {
          this.orgService.state.filter.filters.push({
            field: 'industry',
            operator: 'eq',
            value: selectedIndustries[0].id,
          });
        } else {
          this.orgService.state.filter.filters.push({
            logic: 'or',
            filters: selectedIndustries.map((industry) => ({
              field: 'industry',
              operator: 'eq',
              value: industry.id,
            })),
          });
        }

        this.orgService.read((result) => {
          this.selectedOrganizations.set((result.data as Organization[]) || []);
        });
      },
      { allowSignalWrites: true }
    );

    // Effect 3: Tool changes - loads organizations with selected tools
    effect(
      () => {
        const tools = this.selectedTools();
        const currentOrgId = this.organizationId(); // Capture current organization ID from signal

        if (tools.length === 0) {
          this.toolEnabledOrgs.set([]);
          return;
        }

        if (!this.secondaryOrgService.state.filter) {
          this.secondaryOrgService.state.filter = { logic: 'and', filters: [] };
        }

        const filterParts = tools.map((tool) => `products eq ${tool.id || tool}`);
        const filterString = filterParts.join(' or ');
        this.secondaryOrgService.customFilter = filterString;
        this.secondaryOrgService.appendQueryString = `parent_id=${currentOrgId}`;

        this.secondaryOrgService.read((data) => {
          const orgIds = data.data.map((org) => org.id);
          this.toolEnabledOrgs.set(orgIds);
        });
      },
      { allowSignalWrites: true }
    );
  }

  ngOnInit(): void {
    this.route.queryParams
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter((params) => params['organizationId'] !== undefined),
        map((params) => +params['organizationId']) // Convert to number
      )
      .subscribe((orgId) => {
        this.organizationId.set(orgId);
        this.productService.setOrganizationId(this.organizationId());
        this.productService.read((x) => {
          this.products.set(x.data);
          if (x.data && x.data.length > 0) {
            const firstGroup = x.data[0].group;
            this.selectedServiceGroups.set([firstGroup]);

            setTimeout(() => {
              if (this.toolsMultiselect) {
                this.toolsMultiselect.data = this.filteredProductsByService();
              }
            });
          }
        });
        // Get partner data using initial startDate and endDate
        // Note: applyFilters will calculate the correct initial dates based on selectedTimeRange
        // before these calls are made if we keep applyFilters() below.
        // Let's call applyFilters first to set the dates, then call the API methods.
        // This ensures the initial load uses the default 'Last 30 days'.

        // Set initial date range based on default selection and trigger initial API calls
        this.applyFilters(); // This will set initial dates AND call the API methods with those dates
      });
    this.loadindustryItems();
  }

  // Original API call methods removed, the corrected versions accepting dates are below

  private loadindustryItems() {
    this.generalOrgService
      .getIndustries()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (industries: any) => {
          this.industries.set(industries.results as IndustryResult[]);
        },
        error: (error: any) => {
          console.error('Error fetching industries:', error);
        },
      });
  }

  public applyFilters(): void {
    const value = this.selectedTimeRange().value;

    if (value === '7d') {
      this.startDate.set(new Date(new Date().setDate(new Date().getDate() - 7)));
      this.endDate.set(new Date());
    } else if (value === '30d') {
      this.startDate.set(new Date(new Date().setDate(new Date().getDate() - 30)));
      this.endDate.set(new Date());
    } else if (value === '90d') {
      this.startDate.set(new Date(new Date().setDate(new Date().getDate() - 90)));
      this.endDate.set(new Date());
    }

    if (this.organizationId() && this.startDate() && this.endDate()) {
      // UPDATED: Call all reactive methods individually for consistent filtering
      this.getTicketTrends();
      this.getAlertStatSummary();
      this.getPartnerMttd();
      this.getAverageMttr();
      this.getSlaAdherenceV2();
      this.getPartnerOrgCount();

      // These use different filtering logic, so they stay separate
      this.getOutcomeStatus();
      this.getTopUnknownRateCustomers();

      this.startDateString = this.startDate()?.toISOString() || '';
      this.endDateString = this.endDate()?.toISOString() || '';
    }
  }

  // NEW: Event handlers for the reusable analytics filter component
  public onTimeRangeChange(selectedRange: TimeRange): void {
    this.selectedTimeRange.set(selectedRange);
    // Removed applyFilters() call - it will only be called by the button now
  }

  // Update startDate when the date picker value changes
  public onStartDateChange(newDate: Date | null): void {
    if (newDate) {
      newDate.setHours(0, 0, 0, 0);
      this.startDate.set(newDate);
    } else {
      this.startDate.set(null);
    }
    // No API call here
  }

  // Update endDate when the date picker value changes
  public onEndDateChange(newDate: Date | null): void {
    if (newDate) {
      newDate.setHours(23, 59, 59, 999);
      this.endDate.set(newDate);
    } else {
      this.endDate.set(null);
    }
    // No API call here
  }

  // NEW: Additional event handlers for the reusable analytics filter component
  public onIndustryChange(industries: any[]): void {
    this.selectedIndustries.set(industries || []);
  }

  public onOrganizationsChange(organizations: any[]): void {
    this.selectedOrganizations.set(organizations);
  }

  public onServiceGroupChange(serviceGroups: string[]): void {
    this.selectedServiceGroups.set(serviceGroups);
  }

  public onToolsChange(tools: any[]): void {
    this.selectedTools.set(tools);
  }

  public onApplyFilters(filterState: FilterState): void {
    // Apply filters and refresh data
    this.applyFilters();
  }

  public onResetFilters(): void {
    // Reset all filters to default values
    this.selectedTimeRange.set({ text: 'Last 30 days', value: '30d' });
    this.startDate.set(new Date(new Date().setDate(new Date().getDate() - 30)));
    this.endDate.set(new Date());
    this.selectedIndustries.set([]);
    this.selectedOrganizations.set([]);
    this.selectedServiceGroups.set([]);
    this.selectedTools.set([]);

    // Apply the reset filters
    this.applyFilters();
  }

  private getSelectedOrgIds(): number[] {
    const selectedOrgs = this.selectedOrganizations();
    return selectedOrgs.length > 0 ? (selectedOrgs.map((org) => org.id).filter((id) => id !== null) as number[]) : [];
  }

  private getFallbackOrgIds(selectedOrgIds: number[]): number[] {
    const currentOrgId = this.organizationId();
    return selectedOrgIds.length > 0 ? selectedOrgIds : currentOrgId ? [currentOrgId] : [];
  }

  /**
   * Convert selected tools to security_tool parameter format for backend API calls
   * Two format options available - backend will determine which is needed
   */
  public getSecurityToolsParameter(): string | undefined {
    const tools = this.selectedTools();
    if (tools.length === 0) {
      return undefined;
    }

    // OPTION 1: Product IDs format: 2,3 (currently commented out)
    return tools.map((tool) => tool.id || tool).join(',');

    // OPTION 2: Quoted product names format: 'sentinelone','stellar cyber' (currently active)
    // return tools.map((tool) => `'${tool.name || tool}'`).join(',');
  }

  private getTicketTrends() {
    // SIMPLIFIED: Use reactive filteredOrgIds instead of complex manual filtering
    const orgIds = this.filteredOrgIds();
    const currentQueryType = this.queryType();

    this.fetchAllPartnerData(orgIds, currentQueryType);
  }

  private fetchAllPartnerData(orgIds: number | number[], queryType: QueryType) {
    // SIMPLIFIED: Now just handles ticket trends since other methods call their own fetch functions
    this.fetchTicketTrends(orgIds, queryType);
  }

  private fetchTicketTrends(orgIds: number | number[], queryType: QueryType) {
    const securityTool = this.getSecurityToolsParameter();

    // Set loading state before fetching
    this.ticketTrendsLoaded.set(false);
    this.isFunnelDataLoading.set(true);

    this.analytics
      .getTicketTrendsAnalytics(
        orgIds,
        this.startDate()?.toISOString() || '',
        this.endDate()?.toISOString() || '',
        queryType,
        securityTool
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (trends) => {
          this.ticketTrends.set(trends);
          this.ticketTrendsLoaded.set(true);
          this.checkFunnelDataLoaded();
        },
        error: (error) => {
          console.error('Error fetching partner ticket trends:', error);
          this.ticketTrendsLoaded.set(true); // Mark as loaded even on error
          this.checkFunnelDataLoaded();
        },
      });
  }

  private getAlertStatSummary() {
    // FIXED: Use reactive filteredOrgIds instead of skipping when tools selected
    const orgIds = this.filteredOrgIds();
    const currentQueryType = this.queryType();

    this.fetchAlertStatSummary(orgIds, currentQueryType);
  }

  private fetchAlertStatSummary(orgIds: number | number[], queryType: QueryType) {
    const securityTool = this.getSecurityToolsParameter();

    // Set loading state before fetching
    this.alertStatSummaryLoaded.set(false);
    this.isFunnelDataLoading.set(true);

    this.analytics
      .getAlertStatSummary(
        orgIds,
        this.startDate()?.toISOString() || '',
        this.endDate()?.toISOString() || '',
        queryType,
        securityTool
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (summary) => {
          this.alertStatSummary.set(summary);
          this.alertStatSummaryLoaded.set(true);
          this.checkFunnelDataLoaded();
        },
        error: (error) => {
          console.error('Error fetching partner alert stat summary:', error);
          this.alertStatSummaryLoaded.set(true); // Mark as loaded even on error
          this.checkFunnelDataLoaded();
        },
      });
  }

  /**
   * Check if both data sources for the distillation funnel have loaded
   * Only set loading to false when BOTH are complete
   */
  private checkFunnelDataLoaded(): void {
    if (this.ticketTrendsLoaded() && this.alertStatSummaryLoaded()) {
      this.isFunnelDataLoading.set(false);
    }
  }

  private getPartnerMttd() {
    // FIXED: Use reactive filteredOrgIds instead of skipping when tools selected
    const orgIds = this.filteredOrgIds();
    const currentQueryType = this.queryType();

    this.fetchPartnerMttd(orgIds, currentQueryType);
  }

  private fetchPartnerMttd(orgIds: number | number[], queryType: QueryType) {
    const securityTool = this.getSecurityToolsParameter();

    this.analytics
      .getPartnerMttd(
        orgIds,
        this.startDate()?.toISOString() || '',
        this.endDate()?.toISOString() || '',
        queryType,
        securityTool
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (mttd) => {
          this.partnerMttd.set(mttd);
        },
        error: (error) => {
          console.error('Error fetching partner MTTD:', error);
        },
      });
  }

  private getAverageMttr() {
    // FIXED: Use reactive filteredOrgIds instead of skipping when tools selected
    const orgIds = this.filteredOrgIds();
    const currentQueryType = this.queryType();

    this.fetchAverageMttr(orgIds, currentQueryType);
  }

  private fetchAverageMttr(orgIds: number | number[], queryType: QueryType) {
    const securityTool = this.getSecurityToolsParameter();

    this.analytics
      .getAverageMttr(
        orgIds,
        this.startDate()?.toISOString() || '',
        this.endDate()?.toISOString() || '',
        queryType,
        securityTool
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (mttr) => {
          this.averageMttr.set(mttr);
        },
        error: (error) => {
          console.error('Error fetching partner MTTR:', error);
        },
      });
  }

  private getSlaAdherenceV2() {
    // FIXED: Use reactive filteredOrgIds instead of skipping when tools selected
    const orgIds = this.filteredOrgIds();
    const currentQueryType = this.queryType();

    this.fetchSlaAdherenceV2(orgIds, currentQueryType);
  }

  private fetchSlaAdherenceV2(orgIds: number | number[], queryType: QueryType) {
    const securityTool = this.getSecurityToolsParameter();

    this.analytics
      .getSlaAdherenceV2(
        orgIds,
        this.startDate()?.toISOString() || '',
        this.endDate()?.toISOString() || '',
        queryType,
        securityTool
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (slaAdherence) => {
          this.slaAdherence.set(slaAdherence);
        },
        error: (error) => {
          console.error('Error fetching partner SLA Adherence:', error);
        },
      });
  }

  private getPartnerOrgCount() {
    // FIXED: Use reactive filteredOrgIds instead of skipping when tools selected
    const orgIds = this.filteredOrgIds();
    const currentQueryType = this.queryType();

    this.fetchPartnerOrgCount(orgIds, currentQueryType);
  }

  private fetchPartnerOrgCount(orgIds: number | number[], queryType: QueryType) {
    const securityTool = this.getSecurityToolsParameter();

    this.analytics
      .getPartnerOrgCount(
        orgIds,
        this.startDate()?.toISOString() || '',
        this.endDate()?.toISOString() || '',
        queryType,
        securityTool
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (orgCount) => {
          this.partnerOrgCount.set(orgCount);
        },
        error: (error) => {
          console.error('Error fetching partner Org Count:', error);
        },
      });
  }

  private getOutcomeStatus() {
    const selectedOrgs = this.selectedOrganizations();
    const orgIds =
      selectedOrgs.length > 0
        ? (selectedOrgs.map((org) => org.id).filter((id) => id !== null) as number[])
        : this.organizationId();

    this.analytics
      .getOutcomeStatusMSP(orgIds, this.startDate()?.toISOString() || '', this.endDate()?.toISOString() || '')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.outcomeStatusData.set(response);
        },
        error: (error) => {
          console.error('Error fetching Outcome Status:', error);
        },
      });
  }

  private getTopUnknownRateCustomers() {
    const selectedOrgs = this.selectedOrganizations();
    const orgIds =
      selectedOrgs.length > 0
        ? (selectedOrgs.map((org) => org.id).filter((id) => id !== null) as number[])
        : this.organizationId();

    this.analytics
      .getTopUnknownRateCustomers(orgIds, this.startDate()?.toISOString() || '', this.endDate()?.toISOString() || '')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.unknownRateData.set(response.data);
          this.topTicketInfo.set(response.ticket_info);
        },
        error: (error) => {
          console.error('Error fetching Top Unknown Rate Customers:', error);
        },
      });
  }

  onContextMenuSelect(event: any) {
    let componentRef: any = null;
    // Determine the component reference based on the event name
    switch (event.name) {
      case 'escalationoutcomes':
        componentRef = this.outcomeStatusComponent;
        break;
      default:
        console.warn(`No matching component for event name: ${event.name}`);
        break;
    }

    // If a valid componentRef is set, call the common method
    if (componentRef && typeof componentRef.onContextMenuSelect === 'function') {
      componentRef.onContextMenuSelect(event);
    } else {
      console.warn(
        `Component for event name "${event.name}" is not available or does not implement onContextMenuSelect.`
      );
    }
  }
}
